import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isDevice } from '../../helpers';
import { v4 as uuidv4 } from 'uuid';

const Dropdown = ({
  label,
  placeholder,
  selectedOption,
  options = [],
  onChange,
  disabled = false,
  compareAttr = 'value',
  innerLabel = 'Sort by:',
  style
}) => {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(null);
  const guid = useRef(uuidv4());
  const listboxGuid = useRef(uuidv4());
  const nativeEl = useRef(null);
  const dropdownEl = useRef(null);
  const triggerEl = useRef(null);
  const listingEl = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      const container = dropdownEl.current;

      if (container) {
        if (container !== e.target && !container.contains(e.target)) {
          setOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open) {
      listingEl.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (selectedOption) {
      const index = options.findIndex(
        option => option[compareAttr] === selectedOption[compareAttr]
      );
      setCursor(index);
    } else {
      setCursor(null);
    }
  }, [compareAttr, options, selectedOption]);

  const handleTriggerClick = e => {
    // if (isDevice()) {
    //   return;
    // }

    setOpen(!open);
  };

  const handleOptionClick = option => {
    onChange(option);
    setOpen(false);
  };

  const handleOptionChange = e => {
    const option = options.find(option => option.value === e.target.value);
    onChange(option);
    setOpen(false);
  };

  const handleKeyDown = e => {
    /* eslint-disable indent */
    switch (e.keyCode) {
      case 38: // up
        e.preventDefault();
        if (cursor) {
          setCursor(cursor - 1);
        } else {
          setCursor(options.length - 1);
        }
        break;
      case 40: // down
        e.preventDefault();
        if (cursor !== null && cursor < options.length - 1) {
          setCursor(cursor + 1);
        } else {
          setCursor(0);
        }
        break;
      case 27: // esc
        e.preventDefault();
        setOpen(false);
        triggerEl.current.focus();
        break;
      case 13: // enter
        e.preventDefault();
        if (cursor !== null) {
          onChange(options[cursor]);
        }
        setOpen(false);
        triggerEl.current.focus();
        break;
      case 9: // tab
        setOpen(false);
        break;
    }
    /* eslint-enable indent */
  };

  const getDisplayLabel = () => {
    if (selectedOption) {
      if (innerLabel) {
        return `${innerLabel} ${selectedOption.label}`;
      }
      return `${selectedOption.label}`;
    }
    return placeholder || null;
  };

  return (
    <div className="form-group form-group--dropdown form-group--dropdown-outlined ">
      <label htmlFor={guid.current} className="form-label">
        {label}
      </label>
      <div
        className={`dropdown${isDevice() ? ' dropdown--native' : ''}${
          style ? ` dropdown--${style}` : ''
        }`}
        ref={dropdownEl}
      >
        <select
          id={guid.current}
          onChange={handleOptionChange}
          value={selectedOption ? selectedOption.Value : ''}
          tabIndex="-1"
          ref={nativeEl}
        >
          {options.map((option, i) => {
            return (
              <option key={i} value={option.value}>
                {typeof option === 'object' ? option.label : option}
              </option>
            );
          })}
        </select>
        <button
          className="dropdown__trigger"
          role="combobox"
          aria-label={`${label || ''} ${getDisplayLabel()}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxGuid.current}
          onClick={handleTriggerClick}
          disabled={disabled}
          ref={triggerEl}
        >
          {getDisplayLabel()}
        </button>
        <ul
          role="listbox"
          id={listboxGuid.current}
          aria-activedescendant={
            cursor !== null ? `${guid.current}-${cursor}` : null
          }
          tabIndex="-1"
          className="dropdown__list"
          ref={listingEl}
          onKeyDown={handleKeyDown}
        >
          {options.map((option, i) => (
            <li
              role="option"
              key={i}
              id={`${guid.current}-${i}`}
              className="dropdown__item"
              aria-selected={cursor === i}
              onClick={() => handleOptionClick(option)}
            >
              {typeof option === 'object' ? option.label : option}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selectedOption: PropTypes.shape({
    value: PropTypes.PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool
    ]),
    label: PropTypes.string
  }),
  options: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  compareAttr: PropTypes.string,
  style: PropTypes.string
};

export default Dropdown;
